import { useMemo } from 'react';
import { useLocation } from 'react-router';
import type {
    TileViewModel,
    TileableModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { useDirectoryViewMore } from '~/ui-rocco/pages/category-page/hooks/useProductViewMore';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import {
    DirectoryUniformGrid,
    type DirectoryGridColumns,
} from '~/ui-rocco/pages/category-page/DirectoryUniformGrid';
import { useDirectoryGridInjections } from './use-directory-grid-injections';
import type { DirectoryEntityType } from './DirectoryBrowsePage';

/** Grid page size for professionals/projects (matches the browse loader). */
const DIRECTORY_PAGE_SIZE = 36;

interface DirectoryBrowseGridProps {
    entityType: DirectoryEntityType;
    /**
     * Desktop columns — the page default (4 professionals, 3 projects) or the
     * user's Display dropdown selection (2–6).
     */
    columns: DirectoryGridColumns;
    /** Plural noun for the end-of-list message ("professionals", "projects"). */
    entityNoun: string;
    /** Tile media aspect ratio to stamp on each tile (e.g. "portrait"). */
    aspectRatio: NonNullable<TileViewModel<TileableModel>['aspectRatio']>;
    /** Directory category path (e.g. "/professionals"). */
    categoryLink: string;
    /** First page of raw tile models from the loader. */
    initialTiles: TileableModel[];
    /** Total results across the catalogue. */
    totalCount: number;
    /** Active facet filters carried into View More fetches. */
    filters?: Record<string, unknown>;
    /** In-listing keyword term, carried into View More fetches with them. */
    search?: string;
    /** Active sort from the URL (Sort dropdown), carried into View More fetches. */
    sortBy?: string;
    /**
     * First grid page hydrated on the server (1-indexed). Pages above render as
     * lazy-loading skeletons. 1 unless the URL deep-linked via `?page=N`.
     */
    firstLoadedPage?: number;
    /** Page the URL asked for (1-indexed), scrolled to on mount. Default 1. */
    requestedPage?: number;
    /** Active category name for the end-of-list "…in {label}" prompt. */
    activeCategoryLabel?: string;
    /**
     * Projects currently pinned to the top of the directory — drives the
     * admin context menu's Pin/Unpin label on project tiles.
     */
    featuredProjectIds?: number[];
    /**
     * IDs mid Remove-From-Library fade-out — the tile stays mounted at
     * opacity-0 so the animation can play.
     */
    removedFromLibraryIds?: Set<number>;
    /**
     * IDs whose removal fade has finished — dropped from the grid so it
     * reflows and fills the empty cell.
     */
    unmountedFromLibraryIds?: Set<number>;
    /** Admin action: pin/unpin a project to the top of the directory. */
    onToggleFeature?: (projectId: number, feature: boolean) => void;
    /** Admin action: soft-hide a project/product from the directory. */
    onRemoveFromLibrary?: (
        itemType: 'project' | 'product',
        itemId: number,
        itemName: string,
    ) => void;
}

/**
 * The professionals/projects browse grid: reuses the products View More
 * machinery (infinite scroll + deep-link skeletons + POP restore) and the
 * admin/follow tiles, rendered through the generalized DirectoryUniformGrid at
 * the entity's column count and tile aspect.
 */
export const DirectoryBrowseGrid = ({
    entityType,
    columns,
    entityNoun,
    aspectRatio,
    categoryLink,
    initialTiles,
    totalCount,
    filters,
    search,
    sortBy,
    firstLoadedPage = 1,
    requestedPage = 1,
    activeCategoryLabel,
    featuredProjectIds,
    removedFromLibraryIds,
    unmountedFromLibraryIds,
    onToggleFeature,
    onRemoveFromLibrary,
}: DirectoryBrowseGridProps) => {
    const injections = useDirectoryGridInjections();

    // Wrap the loader's raw models as tile view models for the hook.
    const wrappedInitial = useMemo<TileViewModel<TileableModel>[]>(
        () => initialTiles.map((model) => ({ model })),
        [initialTiles],
    );

    const {
        products: viewMoreTiles,
        hasMore,
        isLoading,
        error,
        loadMore,
        loadSkeletonPage,
        loadingPages,
        currentFirstLoadedPage,
        nextPage,
    } = useDirectoryViewMore({
        entityType,
        pageSize: DIRECTORY_PAGE_SIZE,
        initialProducts: wrappedInitial,
        totalCount,
        categoryLink,
        filters,
        search,
        sortBy,
        firstLoadedPage,
        // The uniform grid keeps `?page=N` in step with the page actually in
        // view (useViewportPageSync) — the request-time rewrite would run
        // ahead of the reader under infinite scroll.
        updateUrlOnLoad: false,
    });

    // Crawlable `?page=N` anchor for the Load More control — mirrors the
    // deep-link URL a click loads, so bots can walk the pages without JS.
    const location = useLocation();
    const nextPageHref = useMemo(() => {
        const params = new URLSearchParams(location.search);
        params.set('page', String(nextPage));
        return `${location.pathname}?${params.toString()}`;
    }, [location.pathname, location.search, nextPage]);

    // Enrich with admin/follow data, then stamp the entity's tile aspect. The
    // media keeps the shared tile radius. Tiles whose Remove-From-Library fade
    // has finished are dropped so the grid reflows and fills the empty cell.
    const withAdmin = useTilesAdminData(viewMoreTiles);
    const tiles = useMemo(
        () =>
            withAdmin
                .filter(
                    (tile) =>
                        !(unmountedFromLibraryIds?.has(tile.model.id) ?? false),
                )
                .map((tile) => ({
                    ...tile,
                    aspectRatio,
                })),
        [withAdmin, aspectRatio, unmountedFromLibraryIds],
    );

    return (
        <DirectoryUniformGrid
            productTiles={tiles}
            // The Display selection renders exactly as chosen — no sidebar
            // clamp. With the sidebar open the tiles just get narrower.
            columns={columns}
            // Products keep 2-up (compare-and-shop); projects/professionals go
            // single column below md so the imagery leads.
            mobileColumns={entityType === 'products' ? 2 : 1}
            // Projects use the narrower 22px column gap and landscape tiles;
            // both are per-vertical constants, unaffected by the column count.
            gapVariant={entityType === 'projects' ? 'projects' : 'standard'}
            skeletonAspectClass={
                entityType === 'projects'
                    ? 'aspect-tile-project-browse'
                    : undefined
            }
            entityNoun={entityNoun}
            pageSize={DIRECTORY_PAGE_SIZE}
            totalCount={totalCount}
            hasMore={hasMore}
            isLoading={isLoading}
            onLoadMore={loadMore}
            hybridLoad
            nextPageHref={nextPageHref}
            // This instance owns ?page (its view-more hook has the
            // request-time rewrite off); tracking keeps the old write's
            // category identity (the category link, not the raw pathname).
            syncPageParam
            trackCategory={categoryLink}
            error={error}
            firstLoadedPage={currentFirstLoadedPage}
            requestedPage={requestedPage}
            onLoadSkeletonPage={loadSkeletonPage}
            loadingPages={loadingPages}
            injections={injections}
            activeCategoryLabel={activeCategoryLabel}
            featuredProjectIds={featuredProjectIds}
            removedFromLibraryIds={removedFromLibraryIds}
            onToggleFeature={onToggleFeature}
            onRemoveFromLibrary={onRemoveFromLibrary}
        />
    );
};
